// @ts-check

/** @file Builds immutable board presentation data from project-replica rows. */

/** @import LocalBoard from "../../../models/board" */
/** @import LocalBoardColumn from "../../../models/board-column" */
/** @import LocalTask from "../../../models/task" */
/** @import LocalTaskBoard from "../../../models/task-board" */

/**
 * @typedef {object} LocalProjectBoardData
 * @property {InstanceType<typeof LocalBoard>} board Board metadata from the captured replica.
 * @property {string[]} columnIds Ordered board-column identifiers.
 * @property {Record<string, InstanceType<typeof LocalBoardColumn>>} columnsById Columns keyed by durable id.
 * @property {Record<string, string[]>} taskBoardIdsByColumnId Ordered placement ids keyed by column id.
 * @property {Record<string, LocalTaskBoardReadView>} taskBoardsById Placement views keyed by durable id.
 * @property {Record<string, string[]>} taskBoardIdsByTaskId Placement ids keyed by task id.
 */

/** Combines tenant-local placement and task rows without creating a command model. */
export class LocalTaskBoardReadView {
  /**
   * Captures one placement/task pair from the same immutable tenant handle.
   * @param {{placement: InstanceType<typeof LocalTaskBoard>, task: InstanceType<typeof LocalTask>}} args Local rows.
   */
  constructor({placement, task}) {
    this.placement = placement
    this.taskRecord = task
  }

  /**
   * Reads the owning column foreign key.
   * @returns {string} Value used to group the placement in the board layout.
   */
  boardColumnId() { return this.placement.boardColumnId() }

  /**
   * Reads the owning board foreign key.
   * @returns {string} Value that scopes the placement to its board.
   */
  boardId() { return this.placement.boardId() }

  /**
   * Reads the identity used for local joins and React keys.
   * @returns {string} Durable placement identifier.
   */
  id() { return this.placement.id() }

  /**
   * Reads the deterministic saved position.
   * @returns {number} One-based position within the owning column.
   */
  rowNumber() { return this.placement.rowNumber() }

  /**
   * Reads the task captured with this placement projection.
   * @returns {InstanceType<typeof LocalTask>} Tenant-local task row.
   */
  task() { return this.taskRecord }

  /**
   * Reads the task foreign key.
   * @returns {string} Value used to join the captured task row.
   */
  taskId() { return this.placement.taskId() }
}

/**
 * Builds board presentation data from independently queried tenant rows.
 * @param {{board: InstanceType<typeof LocalBoard>, columns: InstanceType<typeof LocalBoardColumn>[], taskBoards: InstanceType<typeof LocalTaskBoard>[], tasks: InstanceType<typeof LocalTask>[]}} args Captured local rows.
 * @returns {LocalProjectBoardData} Normalized local board presentation data.
 */
export function createLocalBoardData({board, columns, taskBoards, tasks}) {
  const tasksById = Object.fromEntries(tasks.map((task) => [task.id(), task]))
  /** @type {LocalProjectBoardData} */
  const boardData = {
    board,
    columnIds: [],
    columnsById: {},
    taskBoardIdsByColumnId: {},
    taskBoardIdsByTaskId: {},
    taskBoardsById: {}
  }

  for (const column of columns.slice().sort(compareColumns)) {
    boardData.columnIds.push(column.id())
    boardData.columnsById[column.id()] = column
    boardData.taskBoardIdsByColumnId[column.id()] = []
  }
  for (const placement of taskBoards.slice().sort(compareTaskBoards)) {
    const task = tasksById[placement.taskId()]
    const columnKey = placement.boardColumnId()
    if (!task || !boardData.columnsById[columnKey]) continue

    const taskBoard = new LocalTaskBoardReadView({placement, task})
    boardData.taskBoardsById[taskBoard.id()] = taskBoard
    boardData.taskBoardIdsByColumnId[columnKey].push(taskBoard.id())
    ;(boardData.taskBoardIdsByTaskId[taskBoard.taskId()] ||= []).push(taskBoard.id())
  }

  return boardData
}

/**
 * Translates a filtered-card insertion into the complete persisted column order.
 * @template {{id: () => string}} TaskBoardRecord - Minimal placement read contract.
 * @param {{fullTaskBoards: TaskBoardRecord[], movedTaskBoard: TaskBoardRecord, visibleInsertionIndex: number, visibleTaskBoards: TaskBoardRecord[]}} args Ordering inputs.
 * @returns {number} One-based row number in the complete column.
 * @throws {Error} When a visible anchor is absent from the complete order.
 */
export function fullRowNumberForVisibleInsertion({fullTaskBoards, movedTaskBoard, visibleInsertionIndex, visibleTaskBoards}) {
  const fullTaskBoardsWithoutMoved = fullTaskBoards.filter((candidate) => candidate.id() !== movedTaskBoard.id())
  const visibleTaskBoardsWithoutMoved = visibleTaskBoards.filter((candidate) => candidate.id() !== movedTaskBoard.id())
  const nextVisibleTaskBoard = visibleTaskBoardsWithoutMoved[visibleInsertionIndex]

  if (nextVisibleTaskBoard) {
    return fullRowNumberBeforeVisibleTaskBoard(fullTaskBoardsWithoutMoved, nextVisibleTaskBoard)
  }

  const previousVisibleTaskBoard = visibleTaskBoardsWithoutMoved[visibleInsertionIndex - 1]
  if (previousVisibleTaskBoard) {
    return fullRowNumberBeforeVisibleTaskBoard(fullTaskBoardsWithoutMoved, previousVisibleTaskBoard) + 1
  }

  return fullTaskBoardsWithoutMoved.length + 1
}

/**
 * Finds the one-based full row immediately before a visible anchor.
 * @template {{id: () => string}} TaskBoardRecord - Minimal placement read contract.
 * @param {TaskBoardRecord[]} fullTaskBoards Complete column order excluding the moved row.
 * @param {TaskBoardRecord} visibleTaskBoard Visible anchor.
 * @returns {number} One-based insertion row.
 * @throws {Error} When the anchor is absent from the complete order.
 */
function fullRowNumberBeforeVisibleTaskBoard(fullTaskBoards, visibleTaskBoard) {
  const fullIndex = fullTaskBoards.findIndex((candidate) => candidate.id() === visibleTaskBoard.id())

  if (fullIndex === -1) {
    throw new Error(`Expected visible task board ${visibleTaskBoard.id()} in full board order`)
  }

  return fullIndex + 1
}

/**
 * Returns columns in deterministic display order.
 * @param {LocalProjectBoardData | null} boardData Normalized projection to inspect.
 * @returns {InstanceType<typeof LocalBoardColumn>[]} Ordered columns.
 */
export function localBoardColumns(boardData) {
  if (!boardData) return []
  return boardData.columnIds
    .map((columnId) => boardData.columnsById[columnId])
    .filter(Boolean)
    .sort(compareColumns)
}

/**
 * Returns placements in deterministic display order for one column.
 * @param {LocalProjectBoardData | null} boardData Normalized projection to inspect.
 * @param {string} columnId Column to read.
 * @returns {LocalTaskBoardReadView[]} Ordered placement views.
 */
export function localTaskBoardsForColumn(boardData, columnId) {
  if (!boardData) return []
  return (boardData.taskBoardIdsByColumnId[columnId] || [])
    .map((taskBoardId) => boardData.taskBoardsById[taskBoardId])
    .filter(Boolean)
    .sort(compareTaskBoards)
}

/**
 * Finds the first placement for a task on the current board.
 * @param {LocalProjectBoardData | null} boardData Normalized projection to search.
 * @param {number | string} taskId Task identifier.
 * @returns {LocalTaskBoardReadView | null} Matching placement view.
 */
export function localTaskBoardForTaskId(boardData, taskId) {
  if (!boardData) return null
  const taskBoardId = boardData.taskBoardIdsByTaskId[String(taskId)]?.[0]
  return taskBoardId ? boardData.taskBoardsById[taskBoardId] || null : null
}

/**
 * Compares deterministic column position and id tie-breakers.
 * @param {InstanceType<typeof LocalBoardColumn>} left First candidate.
 * @param {InstanceType<typeof LocalBoardColumn>} right Second candidate.
 * @returns {number} Sort direction.
 */
function compareColumns(left, right) {
  return left.position() - right.position() || left.id().localeCompare(right.id())
}

/**
 * Compares deterministic placement row and id tie-breakers.
 * @param {{id: () => string, rowNumber: () => number}} left First candidate.
 * @param {{id: () => string, rowNumber: () => number}} right Second candidate.
 * @returns {number} Sort direction.
 */
function compareTaskBoards(left, right) {
  return left.rowNumber() - right.rowNumber() || left.id().localeCompare(right.id())
}
